import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import {
  getAllUsers,
  getProfilePhoto
} from '../OrgManagement/employeeApiService';
import { PrimaryButton, SearchBar } from '../ui';
import {
  createPermission,
  fetchShiftAssignments,
  fetchUserSiteDetails,
  getAssignedSiteIds,
  getMyGivenPermissions,
  getUserId,
} from './shiftpermissionApi';

import ArrowIcon from '../../assets/icons/arrow.svg';

// Helper function to normalize users (same as DeleteEmployeeProfile)
function normalizeUsers(users) {
  return users.map(u => ({
    ...u,
    user_id: (typeof u.user_id === 'string' && u.user_id) || u.user_id || 'N/A',
    role_name:
      (typeof u.role_name === 'string' && u.role_name) ||
      (typeof u.spec_role === 'string' && u.spec_role) ||
      'Employee',
    display_id: (typeof u.user_id === 'string' && u.user_id) || u.user_id || 'N/A',
  }));
}

// Helper function to parse time string like "9:00 AM" to Date object
function parseTimeString(timeStr) {
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let hour24 = hours;
  if (period === 'PM' && hours !== 12) hour24 += 12;
  if (period === 'AM' && hours === 12) hour24 = 0;
  return { hours: hour24, minutes };
}

// Helper function to format time from hours and minutes
function formatTime(hours, minutes) {
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 || 12;
  const mins = String(minutes).padStart(2, '0');
  return `${hour12}:${mins} ${period}`;
}

// Helper function to check if current time is within shift hours
function isShiftActive(shift, selectedDate) {
  try {
    const startTime = parseTimeString(shift.shift_start_time);
    const endTime = parseTimeString(shift.shift_end_time);
    
    const now = new Date();
    const today = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    
    const startDateTime = new Date(today);
    startDateTime.setHours(startTime.hours, startTime.minutes, 0, 0);
    
    const endDateTime = new Date(today);
    endDateTime.setHours(endTime.hours, endTime.minutes, 0, 0);
    
    // Handle overnight shifts
    if (startDateTime > endDateTime) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }
    
    return now >= startDateTime && now <= endDateTime;
  } catch (e) {
    return false;
  }
}

function CustomDropdown({ options, selected, onSelect, placeholder }) {
  const [open, setOpen] = useState(false);
  
  // Get the selected option for display
  const selectedOption = options.find((opt) => opt.value === selected);
  
  return (
    <div className="w-full sm:w-80 bg-fuchsia-50 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-300 inline-flex flex-col justify-start items-start relative z-10">
      <div
        className="self-stretch px-3 py-3.5 inline-flex justify-between items-center cursor-pointer"
        onClick={() => setOpen((o) => !o)}
        tabIndex={0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex-1 flex flex-col justify-start items-start">
          {selectedOption ? (
            <>
              <div className="text-black text-base font-bold font-['Open_Sans'] leading-normal">
                {selectedOption.siteName || 'Unknown Site'}
              </div>
              <div className="text-black text-sm font-normal font-['Open_Sans'] leading-relaxed mt-0.5">
                Shift Details: {selectedOption.startTime} TO {selectedOption.endTime}
              </div>
            </>
          ) : (
            <div className="text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
              {placeholder || "Select"}
            </div>
          )}
        </div>
        <img
          src={ArrowIcon}
          alt="dropdown-arrow"
          className={`w-5 h-5 transform transition-transform duration-300 flex-shrink-0 ml-2 ${
            open ? 'rotate-180' : 'rotate-0'
          }`}
        />
      </div>
      {open && (
        <div className="self-stretch p-3 bg-white rounded-lg inline-flex flex-col justify-start items-start gap-2 absolute z-20 top-full left-0 w-full mt-1 shadow-lg border border-gray-200">
          {options.map((opt) => (
            <div
              key={opt.value}
              className="self-stretch px-3 py-2 flex flex-col justify-start items-start cursor-pointer hover:bg-gray-100 rounded transition"
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
              role="option"
            >
              <div className="text-black text-base font-bold font-['Open_Sans'] leading-normal">
                {opt.siteName || 'Unknown Site'}
              </div>
              <div className="text-black text-sm font-normal font-['Open_Sans'] leading-relaxed mt-0.5">
                Shift Details: {opt.startTime} TO {opt.endTime}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShiftPermissionManagement() {
  // Log screen view
  useScreenLogging('ShiftPermissionManagement', {
    component: 'ShiftPermissionManagement',
  });
  // Mode toggle
  const [isCreateMode, setIsCreateMode] = useState(true);
  
  // Site and shift data
  const [siteId, setSiteId] = useState(null);
  const [siteName, setSiteName] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeShifts, setActiveShifts] = useState([]);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftStartTime, setShiftStartTime] = useState(null);
  const [shiftEndTime, setShiftEndTime] = useState(null);
  
  // Employee data
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Permission form state
  const [permissionStartTime, setPermissionStartTime] = useState(null);
  const [permissionEndTime, setPermissionEndTime] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [remarksError, setRemarksError] = useState(null);
  const [duplicatePermissionError, setDuplicatePermissionError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // View mode state
  const [viewPermissions, setViewPermissions] = useState([]);
  
  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Mobile view state
  const [isMobileView, setIsMobileView] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Reload shifts and employees when date or shift changes
  useEffect(() => {
    if (siteId && isCreateMode) {
      loadShiftsAndEmployees();
    }
  }, [siteId, selectedDate, isCreateMode]);

  // Update employees when shift changes
  useEffect(() => {
    if (selectedShiftId && selectedShift) {
      loadEmployeesForShift();
    }
  }, [selectedShiftId, selectedShift]);

  // Filter employees based on search
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      setFilteredEmployees(employees);
    } else {
      setFilteredEmployees(
        employees.filter(
          (e) =>
            e.user_name?.toLowerCase().includes(q) ||
            e.employee_id?.toLowerCase().includes(q) ||
            e.user_id?.toLowerCase().includes(q)
        )
      );
    }
  }, [search, employees]);

  // Initialize permission times when shift changes
  useEffect(() => {
    if (shiftStartTime && shiftEndTime) {
      setPermissionStartTime(shiftStartTime);
      setPermissionEndTime(shiftEndTime);
    }
  }, [shiftStartTime, shiftEndTime]);

  useEffect(() => {
    setDuplicatePermissionError(null);
  }, [selectedEmployee, selectedDate, selectedShiftId]);

  // Mobile view detection
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  const loadData = async () => {
      try {
        setIsLoading(true);
      
      const userId = await getUserId();
      if (!userId) throw new Error('User ID not found');
      
      const siteIds = await getAssignedSiteIds(userId);
      if (siteIds.length === 0) throw new Error('No site assigned to this user');
      
      setSiteId(siteIds[0]);
      
      // Load site name
      const siteDetails = await fetchUserSiteDetails(userId);
      if (siteDetails) {
        setSiteName(siteDetails.site_name || 'Unknown Site');
      }
      } catch (err) {
      console.error('Error loading data:', err);
      } finally {
        setIsLoading(false);
    }
  };

  const loadShiftsAndEmployees = async () => {
    if (!siteId) return;
    
    try {
      setIsLoading(true);
      
      const formattedDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate()
      );
      
      const shiftsData = await fetchShiftAssignments(siteId, formattedDate);
      
      // Process shifts - prioritize active shifts, fallback to all shifts
      const now = new Date();
      const shiftsArray = Array.isArray(shiftsData) ? shiftsData : [];
      
      const activeShiftList = shiftsArray.filter((shift) =>
        isShiftActive(shift, formattedDate)
      );
      
      const shiftsToUse =
        activeShiftList.length > 0 ? activeShiftList : shiftsArray;
      
      setActiveShifts(shiftsToUse);
      
      if (shiftsToUse.length > 0) {
        const firstShift = shiftsToUse[0];
        setSelectedShiftId(firstShift.ss_id);
        setSelectedShift(firstShift);
        
        // Parse shift times
        const startTime = parseTimeString(firstShift.shift_start_time);
        const endTime = parseTimeString(firstShift.shift_end_time);
        setShiftStartTime(startTime);
        setShiftEndTime(endTime);
      }
    } catch (err) {
      console.error('Error loading shifts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEmployeesForShift = async () => {
    if (!selectedShift) return;
    
    try {
      setIsLoading(true);
      
      const userIds = selectedShift.user_ids || [];
      if (userIds.length === 0) {
      setEmployees([]);
      setSelectedEmployee(null);
      return;
    }
      
      // Fetch all users (same pattern as DeleteEmployeeProfile)
      // We'll get all users and filter by the shift's user_ids
      let allUsersRaw = [];
      try {
        allUsersRaw = await getAllUsers();
      } catch (error) {
        console.error('Error fetching all users:', error);
        allUsersRaw = [];
      }
      
      // Filter users to only those in the shift's user_ids list
      const shiftUserIdsSet = new Set(userIds.map(id => String(id).toLowerCase().trim()));
      const matchedUsers = (allUsersRaw || []).filter(user => {
        const userId = String(user.user_id || user.employee_id || '').toLowerCase().trim();
        return shiftUserIdsSet.has(userId);
      });
      
      // Normalize users (same as DeleteEmployeeProfile)
      const normalizedUsers = normalizeUsers(matchedUsers);
      
      // Add profile photos (same pattern as DeleteEmployeeProfile)
      const withPhotos = await Promise.all(
        normalizedUsers.map(async (u) => {
          try {
            const photo = await getProfilePhoto(u.user_id);
            return { ...u, profile_image: photo || null };
          } catch {
            return { ...u, profile_image: null };
          }
        })
      );
      
        setEmployees(withPhotos);
        setSelectedEmployee(null);
      } catch (err) {
      console.error('Error loading employees:', err);
      } finally {
        setIsLoading(false);
    }
  };

  const handleShiftChange = (shiftId) => {
    const shift = activeShifts.find((s) => s.ss_id === shiftId);
    if (shift) {
      setSelectedShiftId(shiftId);
      setSelectedShift(shift);
      
      const startTime = parseTimeString(shift.shift_start_time);
      const endTime = parseTimeString(shift.shift_end_time);
      setShiftStartTime(startTime);
      setShiftEndTime(endTime);
      
      setSelectedEmployee(null);
    }
  };

  const handlePermissionStartTimeChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setPermissionStartTime(null);
      setDuplicatePermissionError(null);
      return;
    }
    const [hours, minutes] = value.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes)) {
      setPermissionStartTime({ hours, minutes });
      setDuplicatePermissionError(null);
    }
  };

  const handlePermissionEndTimeChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setPermissionEndTime(null);
      setDuplicatePermissionError(null);
      return;
    }
    const [hours, minutes] = value.split(':').map(Number);
    if (!isNaN(hours) && !isNaN(minutes)) {
      setPermissionEndTime({ hours, minutes });
      setDuplicatePermissionError(null);
      
      // Validate end time is after start time
      if (permissionStartTime) {
        const startMinutes = permissionStartTime.hours * 60 + permissionStartTime.minutes;
        const endMinutes = hours * 60 + minutes;
        
        if (startMinutes <= endMinutes && endMinutes <= startMinutes) {
          // Same time or end before start (same day case)
          // We'll let the validation message show
        }
      }
    }
  };

  const isTimeWithinShift = (time) => {
    if (!time || !shiftStartTime || !shiftEndTime) return false;
    
    const timeMinutes = time.hours * 60 + time.minutes;
    const shiftStartMinutes = shiftStartTime.hours * 60 + shiftStartTime.minutes;
    const shiftEndMinutes = shiftEndTime.hours * 60 + shiftEndTime.minutes;
    
    if (shiftStartMinutes <= shiftEndMinutes) {
      return timeMinutes >= shiftStartMinutes && timeMinutes <= shiftEndMinutes;
    } else {
      // Overnight shift
      return timeMinutes >= shiftStartMinutes || timeMinutes <= shiftEndMinutes;
    }
  };

  const isShiftOvernight = () => {
    if (!shiftStartTime || !shiftEndTime) return false;
    
    const shiftStartMinutes = shiftStartTime.hours * 60 + shiftStartTime.minutes;
    const shiftEndMinutes = shiftEndTime.hours * 60 + shiftEndTime.minutes;
    
    return shiftStartMinutes > shiftEndMinutes;
  };

  const isTimeRangeValid = () => {
    if (!permissionStartTime || !permissionEndTime) return false;
    
    const startMinutes =
      permissionStartTime.hours * 60 + permissionStartTime.minutes;
    const endMinutes = permissionEndTime.hours * 60 + permissionEndTime.minutes;
    
    const shiftOvernight = isShiftOvernight();
    
    if (shiftOvernight) {
      // For overnight shifts, allow wrapping to next day but disallow zero-length range
      return startMinutes !== endMinutes;
    }
    
    return endMinutes > startMinutes;
  };

  // Comprehensive validation that checks both time range and shift boundaries
  const getTimeValidationStatus = () => {
    if (!permissionStartTime || !permissionEndTime) {
      return { isValid: false, message: 'Please select both start and end times' };
    }

    // Check if times are within shift
    const startWithinShift = isTimeWithinShift(permissionStartTime);
    const endWithinShift = isTimeWithinShift(permissionEndTime);

    if (!startWithinShift && !endWithinShift) {
      return { isValid: false, message: 'Both times must be within the shift hours' };
    }
    if (!startWithinShift) {
      return { isValid: false, message: 'Start time must be within the shift hours' };
    }
    if (!endWithinShift) {
      return { isValid: false, message: 'End time must be within the shift hours' };
    }

    // Check if time range is valid (end > start)
    if (!isTimeRangeValid()) {
      return { isValid: false, message: 'End time must be after start time' };
    }

    return { isValid: true, message: 'Time range is valid' };
  };

  const findOverlappingPermission = async (
    userId,
    startDateTime,
    endDateTime
  ) => {
    try {
      const result = await getMyGivenPermissions();
      const permissions = result.permissions || [];
      const normalizedUserId = String(userId || '').toLowerCase().trim();

      const overlappingPermission = permissions.find((permission) => {
        if (!permission?.user_id) return false;
        const permissionUserId = String(permission.user_id)
          .toLowerCase()
          .trim();

        if (permissionUserId !== normalizedUserId) return false;

        const existingStart = new Date(permission.start_timestamp);
        const existingEnd = new Date(permission.end_timestamp);

        return startDateTime < existingEnd && endDateTime > existingStart;
      });

      return overlappingPermission || null;
    } catch (error) {
      console.error('Error checking overlapping permissions:', error);
      return null;
    }
  };

  const handleCreatePermission = async () => {
    if (!selectedEmployee || !selectedShiftId || !siteId) {
      alert('Please select an employee and shift');
      return;
    }

    if (!permissionStartTime || !permissionEndTime) {
      alert('Please select valid times');
      return;
    }

    // Validate times are within shift
    if (!isTimeWithinShift(permissionStartTime) || !isTimeWithinShift(permissionEndTime)) {
      alert('Please select the time range only between the shift');
      return;
    }

    // Validate time range
    if (!isTimeRangeValid()) {
      alert('End time must be after start time');
      return;
    }

    // Validate remarks - at least 2 words
    const remarksTrimmed = remarks.trim();
    const wordCount = remarksTrimmed
      .split(' ')
      .filter((word) => word.length > 0).length;
    
    if (wordCount < 2) {
      setRemarksError('Please enter at least 2 words in remarks');
      return;
    }

    setRemarksError(null);
    setDuplicatePermissionError(null);

    const permissionStartDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        permissionStartTime.hours,
        permissionStartTime.minutes
      );

    const permissionEndDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        permissionEndTime.hours,
        permissionEndTime.minutes
      );

    const overlappingPermission = await findOverlappingPermission(
      selectedEmployee.user_id,
      permissionStartDateTime,
      permissionEndDateTime
    );

    if (overlappingPermission) {
      const existingStart = new Date(overlappingPermission.start_timestamp);
      const existingEnd = new Date(overlappingPermission.end_timestamp);
      const formattedExistingStart = formatTime(
        existingStart.getHours(),
        existingStart.getMinutes()
      );
      const formattedExistingEnd = formatTime(
        existingEnd.getHours(),
        existingEnd.getMinutes()
      );

      setDuplicatePermissionError(
        `A permission has already been created (${formattedExistingStart} to ${formattedExistingEnd}) for the same time or an overlapping time period`
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const permissionData = {
        ss_id: selectedShiftId,
        site_id: siteId,
        user_id: selectedEmployee.user_id,
        start_timestamp: permissionStartDateTime.toISOString(),
        end_timestamp: permissionEndDateTime.toISOString(),
        remarks: remarksTrimmed,
      };

      const result = await createPermission(permissionData);
      
      // Log API event for creating permission
      loggingService.logInfo(
        'permission_create',
        `User created permission for employee ${selectedEmployee.user_id} on shift ${selectedShiftId}`,
        {
          endpoint: '/shift/permission',
          method: 'POST',
          siteId: siteId,
          userId: selectedEmployee.user_id,
          shiftId: selectedShiftId,
          startTimestamp: permissionStartDateTime.toISOString(),
          endTimestamp: permissionEndDateTime.toISOString(),
        }
      ).catch(logError => {
        console.warn('[ShiftPermissionManagement] Failed to log event:', logError);
      });
      
      alert(`Permission Created: ${result.message || 'Success'}`);
      setDuplicatePermissionError(null);
      
      // Reset form
      setSelectedEmployee(null);
      setRemarks('');
      setPermissionStartTime(shiftStartTime);
      setPermissionEndTime(shiftEndTime);
    } catch (error) {
      console.error('Error creating permission:', error);
      alert(`Error creating permission: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewModeSwitch = async () => {
    setIsCreateMode(false);
    await loadViewPermissions();
  };

  const loadViewPermissions = async () => {
    try {
      setIsLoading(true);
      const result = await getMyGivenPermissions();
      
      // Filter out duplicates based on p_id
      const fetchedPermissions = result.permissions || [];
      const uniquePermissions = [];
      const seenIds = new Set();
      
      for (const permission of fetchedPermissions) {
        if (permission.p_id && !seenIds.has(permission.p_id)) {
          uniquePermissions.push(permission);
          seenIds.add(permission.p_id);
        }
      }
      
      // Load employee names and photos for permissions (same pattern as DeleteEmployeeProfile)
      // Fetch all users once and match with permissions
      let allUsersRaw = [];
      try {
        allUsersRaw = await getAllUsers();
      } catch (error) {
        console.error('Error fetching all users for view mode:', error);
      }
      
      // Create a map of users by user_id for quick lookup
      const userMap = {};
      const normalizedUsers = normalizeUsers(allUsersRaw || []);
      normalizedUsers.forEach(user => {
        const userId = String(user.user_id || user.employee_id || '').toLowerCase().trim();
        userMap[userId] = user;
      });
      
      // Match permissions with users and add profile photos
      const permissionsWithDetails = await Promise.all(
        uniquePermissions.map(async (permission) => {
          const userId = String(permission.user_id || '').toLowerCase().trim();
          const matchedUser = userMap[userId];
          
          if (matchedUser) {
            try {
              const photo = await getProfilePhoto(permission.user_id);
              return {
                ...permission,
                user_name: matchedUser.user_name || permission.user_id,
                profile_image: photo || '/assets/images/default_profile.png',
              };
            } catch (e) {
              return {
                ...permission,
                user_name: matchedUser.user_name || permission.user_id,
                profile_image: '/assets/images/default_profile.png',
              };
            }
          } else {
            // User not found in all users, try individual fetch as fallback
            try {
              const photo = await getProfilePhoto(permission.user_id);
              return {
                ...permission,
                user_name: permission.user_id,
                profile_image: photo || '/assets/images/default_profile.png',
              };
            } catch (e) {
              return {
                ...permission,
                user_name: permission.user_id,
                profile_image: '/assets/images/default_profile.png',
              };
            }
          }
        })
      );
      
      setViewPermissions(permissionsWithDetails);
    } catch (error) {
      console.error('Error loading permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const shiftOptions = activeShifts.map((shift) => ({
    value: shift.ss_id,
    siteName: siteName || 'Unknown Site',
    startTime: shift.shift_start_time,
    endTime: shift.shift_end_time,
  }));

  return (
    <div className="mx-auto relative flex flex-col items-center min-h-screen px-2 sm:px-4 py-2 sm:py-4 overflow-x-hidden w-full">
      {/* Mode toggle */}
      <div className="mb-4 sm:mb-6 self-start w-full">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-md mx-auto w-full sm:w-auto">
          <button
            onClick={() => setIsCreateMode(true)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
              isCreateMode
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Create
          </button>
          <button
            onClick={handleViewModeSwitch}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md transition-colors ${
              !isCreateMode
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            View
          </button>
        </div>
      </div>

      {isCreateMode ? (
        <>
          {/* Shift dropdown */}
      <div className="mb-4 sm:mb-6 self-start w-full">
        <div className="text-black text-lg sm:text-xl font-bold font-['Figtree'] leading-normal mb-2">
          Current Shifts
        </div>
        <p className="text-black text-base sm:text-lg font-normal font-['Open_Sans'] leading-relaxed mb-3 sm:mb-4">
          Select the shift to give permission
        </p>
            {activeShifts.length === 0 && !isLoading && (
              <p className="text-red-600 font-bold mb-2 text-sm sm:text-base">No active shift available</p>
            )}
        <div className="w-full sm:w-auto">
          <CustomDropdown
                options={shiftOptions}
                selected={selectedShiftId}
                onSelect={handleShiftChange}
                placeholder="Select Active Shift"
          />
        </div>
      </div>

          {/* Main content */}
      <div className={`w-full flex ${isMobileView ? 'flex-col' : 'flex-row'} gap-4 lg:gap-6 overflow-x-hidden`}>
        {/* Left panel for search/filter/employees */}
        <div className={`flex flex-col ${isMobileView ? 'w-full' : 'w-full sm:w-96'} h-full`}>
          <div className="text-black text-base sm:text-lg font-bold font-['Figtree'] leading-snug mb-3">
            Employees assigned to the shift
          </div>
          <div className="mb-4">
            <SearchBar
              placeholder="Search employee by name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={(value) => setSearch(value)}
              className="w-full sm:w-96"
            />
          </div>
          <div className={`w-full h-[400px] sm:h-[488px] flex flex-col justify-start items-start gap-2 sm:gap-3 md:gap-4 overflow-y-auto pr-2 pb-2`}>
            {isLoading && (
                  <div className="p-4 text-gray-400 text-center w-full font-['Open_Sans'] text-base">
                    Loading...
                  </div>
            )}
            {!isLoading && filteredEmployees.length === 0 && (
                  <div className="p-4 text-gray-400 text-center w-full font-['Open_Sans'] text-base">
                    No employees found.
                  </div>
            )}
                {!isLoading &&
                  filteredEmployees.map((emp) => (
              <div
                key={emp.user_id}
                onClick={() => {
                  setSelectedEmployee(emp);
                  if (isMobileView) {
                    setShowMobileModal(true);
                  }
                }}
                className={
                  'self-stretch p-2 sm:p-2.5 md:p-3 rounded-xl inline-flex justify-start items-center gap-2 sm:gap-3 md:gap-6 cursor-pointer transition ' +
                  (selectedEmployee?.user_id === emp.user_id
                          ? ' bg-[radial-gradient(ellipse_152.09%_184.58%_at_136.24%_-51.30%,rgba(10,128,137,0.09)_0%,rgba(255,255,255,0.72)_100%)] shadow-[2px_2px_12px_0px_rgba(34,139,34,0.12)] outline outline-1 outline-offset-[-1px] outline-white'
                          : ' bg-neutral-50/10 outline outline-1 outline-offset-[-1px] outline-neutral-50/40')
                }
              >
                <img
                  className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl object-cover flex-shrink-0"
                  src={emp.profile_image || '/assets/images/default_profile.png'}
                  alt=""
                />
                <div className="flex-1 inline-flex flex-col justify-start items-start min-w-0">
                        <div className="text-black text-sm sm:text-base md:text-lg font-bold font-['Figtree'] leading-snug truncate w-full">
                          {emp.user_name}
                        </div>
                        <div className="text-black text-xs sm:text-sm md:text-lg font-normal font-['Open_Sans'] leading-relaxed truncate w-full">
                          ID : {emp.employee_id || emp.user_id}
                        </div>
                </div>
              </div>
            ))}
          </div>
        </div>

            {/* Right panel for permission form - Hidden on mobile */}
            {!isMobileView && (
            <div className="flex-1 bg-neutral-50/50 rounded-xl shadow-[-2px_-2px_6px_0px_rgba(211,211,211,0.10)] shadow-[2px_2px_6px_0px_rgba(211,211,211,0.10)] outline outline-1 outline-offset-[-1px] outline-neutral-300 backdrop-blur-[20px] flex flex-col p-4 sm:p-6 lg:p-8 relative overflow-y-auto overflow-x-hidden">
          {!selectedEmployee ? (
            <div className="flex-1 flex items-center justify-center text-gray-400 text-lg sm:text-xl font-['Figtree']">
              Select an employee on the left
            </div>
          ) : (
                <div className="relative flex flex-col gap-4 sm:gap-6 h-full justify-start overflow-x-hidden">
                  {/* Employee info */}
              <div className="flex items-center gap-3 sm:gap-4 overflow-x-hidden w-full">
                <img
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover flex-shrink-0"
                      src={selectedEmployee.profile_image || '/assets/images/default_profile.png'}
                  alt=""
                />
                <div className="min-w-0 flex-1 overflow-hidden">
                      <div className={`${typography.h4} text-black font-bold leading-6 sm:leading-7 truncate`}>
                        {selectedEmployee.user_name}
                      </div>
                      <div className="text-base sm:text-lg text-gray-700 font-semibold font-['Open_Sans'] leading-relaxed break-words">
                        Role: <span className="h-4">{selectedEmployee.role_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Shift time info */}
                  {shiftStartTime && shiftEndTime && (
                    <div className="p-2.5 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
                      <div className="text-xs sm:text-sm text-orange-700 font-semibold break-words">
                        Shift: {formatTime(shiftStartTime.hours, shiftStartTime.minutes)} -{' '}
                        {formatTime(shiftEndTime.hours, shiftEndTime.minutes)}
                      </div>
                    </div>
                  )}

                  {/* Time selection */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={
                          permissionStartTime
                            ? `${String(permissionStartTime.hours).padStart(2, '0')}:${String(permissionStartTime.minutes).padStart(2, '0')}`
                            : ''
                        }
                        onChange={handlePermissionStartTimeChange}
                        className={`w-full p-2 sm:p-2.5 border rounded-lg text-sm sm:text-base ${
                          permissionStartTime && isTimeWithinShift(permissionStartTime)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300'
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={
                          permissionEndTime
                            ? `${String(permissionEndTime.hours).padStart(2, '0')}:${String(permissionEndTime.minutes).padStart(2, '0')}`
                            : ''
                        }
                        onChange={handlePermissionEndTimeChange}
                        className={`w-full p-2 sm:p-2.5 border rounded-lg text-sm sm:text-base ${
                          permissionEndTime && isTimeWithinShift(permissionEndTime)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300'
                        }`}
                      />
                    </div>
                  </div>

                  {/* Time range validation */}
                  {permissionStartTime && permissionEndTime && (() => {
                    const validation = getTimeValidationStatus();
                    return (
                      <div
                        key="time-validation"
                        className={`p-2.5 sm:p-3 rounded-lg ${
                          validation.isValid
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200'
                        }`}
                      >
                        <div
                          className={`text-xs sm:text-sm ${
                            validation.isValid ? 'text-green-700' : 'text-red-700'
                          }`}
                        >
                          {validation.message}
                        </div>
                      </div>
                    );
                  })()}
                  {duplicatePermissionError && (
                    <div className="p-2.5 sm:p-3 rounded-lg bg-red-50 border border-red-200">
                      <div className="text-xs sm:text-sm text-red-700">
                        {duplicatePermissionError}
                      </div>
                    </div>
                  )}

                  {/* Remarks */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remarks
                    </label>
                    {remarksError && (
                      <div className="text-red-600 text-xs sm:text-sm mb-2">{remarksError}</div>
                    )}
                    <textarea
                      value={remarks}
                      onChange={(e) => {
                        setRemarks(e.target.value);
                        setRemarksError(null);
                      }}
                      placeholder="Enter remarks (at least 2 words)"
                      rows={3}
                      className="w-full p-2 sm:p-2.5 border border-gray-300 rounded-lg text-sm sm:text-base resize-none"
                    />
                  </div>

                  {/* Submit button */}
                  <PrimaryButton
                    onClick={handleCreatePermission}
                    disabled={
                      isSubmitting ||
                      !getTimeValidationStatus().isValid ||
                      !permissionStartTime ||
                      !permissionEndTime ||
                      !!duplicatePermissionError ||
                      remarks.trim().split(' ').filter((w) => w.length > 0).length < 2
                    }
                    className="mt-4"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Permission'}
                  </PrimaryButton>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Mobile Modal for Employee Details */}
          {showMobileModal && selectedEmployee && createPortal(
            <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center min-h-screen w-full overflow-y-auto p-2 sm:p-4 bg-black/50" 
              onClick={() => setShowMobileModal(false)} 
              role="dialog" 
              aria-modal="true"
            >
              <div 
                className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                  <h2 className={`${typography.h4} text-base sm:text-lg text-black`}>
                    Employee Details
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowMobileModal(false)}
                    className="text-gray-500 hover:text-black p-1 rounded hover:bg-gray-100"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                </div>
                <div className="p-3 sm:p-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0">
                  <div className="relative bg-neutral-50/50 rounded-xl p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-x-hidden">
                    {/* Employee info */}
                    <div className="flex items-center gap-3 sm:gap-4 overflow-x-hidden w-full">
                      <img
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover flex-shrink-0"
                        src={selectedEmployee.profile_image || '/assets/images/default_profile.png'}
                        alt=""
                      />
                      <div className="min-w-0 flex-1 overflow-hidden">
                          <div className={`${typography.h4} text-black font-bold leading-6 sm:leading-7 truncate`}>
                            {selectedEmployee.user_name}
                          </div>
                        <div className="text-base sm:text-lg text-gray-700 font-semibold font-['Open_Sans'] leading-relaxed break-words">
                          Role: <span className="font-normal break-words">{selectedEmployee.role_name}</span>
                        </div>
                        <div className="text-sm sm:text-base text-black font-normal font-['Open_Sans'] leading-relaxed mt-1 truncate">
                          ID: {selectedEmployee.employee_id || selectedEmployee.user_id}
                        </div>
                      </div>
                    </div>

                    {/* Shift time info */}
                    {shiftStartTime && shiftEndTime && (
                      <div className="p-2.5 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg overflow-hidden">
                        <div className="text-xs sm:text-sm text-orange-700 font-semibold break-words">
                          Shift: {formatTime(shiftStartTime.hours, shiftStartTime.minutes)} -{' '}
                          {formatTime(shiftEndTime.hours, shiftEndTime.minutes)}
                        </div>
                      </div>
                    )}

                    {/* Time selection */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time
                        </label>
                        <input
                          type="time"
                          value={
                            permissionStartTime
                              ? `${String(permissionStartTime.hours).padStart(2, '0')}:${String(permissionStartTime.minutes).padStart(2, '0')}`
                              : ''
                          }
                          onChange={handlePermissionStartTimeChange}
                          className={`w-full p-2 sm:p-2.5 border rounded-lg text-sm sm:text-base ${
                            permissionStartTime && isTimeWithinShift(permissionStartTime)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300'
                          }`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time
                        </label>
                        <input
                          type="time"
                          value={
                            permissionEndTime
                              ? `${String(permissionEndTime.hours).padStart(2, '0')}:${String(permissionEndTime.minutes).padStart(2, '0')}`
                              : ''
                          }
                          onChange={handlePermissionEndTimeChange}
                          className={`w-full p-2 sm:p-2.5 border rounded-lg text-sm sm:text-base ${
                            permissionEndTime && isTimeWithinShift(permissionEndTime)
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-300'
                          }`}
                        />
                      </div>
                    </div>

                    {/* Time range validation */}
                    {permissionStartTime && permissionEndTime && (() => {
                      const validation = getTimeValidationStatus();
                      return (
                        <div
                          key="time-validation"
                          className={`p-2.5 sm:p-3 rounded-lg ${
                            validation.isValid
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-red-50 border border-red-200'
                          }`}
                        >
                          <div
                            className={`text-xs sm:text-sm ${
                              validation.isValid ? 'text-green-700' : 'text-red-700'
                            }`}
                          >
                            {validation.message}
                          </div>
                        </div>
                      );
                    })()}
                    {duplicatePermissionError && (
                      <div className="p-2.5 sm:p-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="text-xs sm:text-sm text-red-700">
                          {duplicatePermissionError}
                        </div>
                      </div>
                    )}

                    {/* Remarks */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Remarks
                      </label>
                      {remarksError && (
                        <div className="text-red-600 text-xs sm:text-sm mb-2">{remarksError}</div>
                      )}
                      <textarea
                        value={remarks}
                        onChange={(e) => {
                          setRemarks(e.target.value);
                          setRemarksError(null);
                        }}
                        placeholder="Enter remarks (at least 2 words)"
                        rows={3}
                        className="w-full p-2 sm:p-2.5 border border-gray-300 rounded-lg text-sm sm:text-base resize-none"
                      />
                    </div>

                    {/* Submit button */}
                    <PrimaryButton
                      onClick={async () => {
                        await handleCreatePermission();
                        if (!duplicatePermissionError && !remarksError) {
                          setShowMobileModal(false);
                        }
                      }}
                      disabled={
                        isSubmitting ||
                        !getTimeValidationStatus().isValid ||
                        !permissionStartTime ||
                        !permissionEndTime ||
                        !!duplicatePermissionError ||
                        remarks.trim().split(' ').filter((w) => w.length > 0).length < 2
                      }
                      className="mt-4"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Permission'}
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </>
      ) : (
        /* View mode */
        <div className="w-full">
          {/* Date filter temporarily disabled
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Date
            </label>
            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => {
                setSelectedDate(new Date(e.target.value));
                loadViewPermissions();
              }}
              className="p-2 border border-gray-300 rounded-lg"
            />
          </div>
          */}

          <div className="w-full h-[500px] sm:h-[600px] overflow-y-auto">
            {isLoading && (
              <div className="p-4 text-gray-400 text-center w-full font-['Open_Sans'] text-sm sm:text-base">
                Loading...
              </div>
            )}
            {!isLoading && viewPermissions.length === 0 && (
              <div className="p-4 text-gray-400 text-center w-full font-['Open_Sans'] text-sm sm:text-base">
                No permissions found.
            </div>
          )}
            {!isLoading &&
              viewPermissions.map((permission) => {
                const startDate = new Date(permission.start_timestamp);
                const endDate = new Date(permission.end_timestamp);
                
                return (
                  <div
                    key={permission.p_id}
                    className="p-3 sm:p-4 mb-3 sm:mb-4 bg-green-100 rounded-lg cursor-pointer hover:bg-green-200 transition"
                    onClick={() => {
                      alert(
                        `Guard: ${permission.user_name}\nSite: ${siteName || 'Unknown'}\nShift Time: ${selectedShift?.shift_start_time || 'N/A'} - ${selectedShift?.shift_end_time || 'N/A'}\nPermission Start: ${startDate.toLocaleString()}\nPermission End: ${endDate.toLocaleString()}\nRemarks: ${permission.remarks || 'N/A'}`
                      );
                    }}
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <img
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                        src={permission.profile_image || '/assets/images/default_profile.png'}
                        alt=""
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-black font-semibold text-base sm:text-lg truncate">
                          {permission.user_name}
                        </div>
                        <div className="text-gray-600 text-xs sm:text-sm">
                          From: {startDate.toLocaleString()}
                        </div>
                        <div className="text-gray-600 text-xs sm:text-sm">
                          To: {endDate.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
